import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Video, Plus, Trash2, Calendar, Clock, Users, Radio,
  Play, Monitor, BookOpen, X, Loader, CheckCircle,
  StopCircle, ExternalLink, RefreshCw,
} from "lucide-react";
import {
  lmsGetLiveClasses, lmsCreateLiveClass, lmsUpdateLiveClass,
  lmsDeleteLiveClass, lmsGetLiveClassJoinUrl, lmsGetLiveClassStatus,
  lmsGetLiveClassRecordings, lmsGetClassSubjects,
} from "../api/lmsApi";
import { fetchLmsSchoolClasses } from "../redux/lmsSlice";
import { useDispatch } from "react-redux";
import "../lms.css";

/* ── helpers ── */
const STATUS_META = {
  scheduled: { label: "Scheduled", color: "#f59e0b", bg: "#fffbeb", icon: Calendar },
  live:      { label: "Live Now",  color: "#16a34a", bg: "#f0fdf4", icon: Radio },
  ended:     { label: "Ended",     color: "#6b7280", bg: "#f3f4f6", icon: StopCircle },
};

function formatDate(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
}

function formatDuration(min) {
  if (!min) return "—";
  const h = Math.floor(min / 60), m = min % 60;
  return h > 0 ? `${h}h ${m > 0 ? m + "m" : ""}`.trim() : `${m}m`;
}

/* ── small input style ── */
const inp = (extra = {}) => ({
  width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13,
  border: "1.5px solid #e5e7eb", outline: "none", color: "#111827",
  background: "#fff", boxSizing: "border-box", ...extra,
});

/* ══ Schedule Modal ══ */
export function ScheduleModal({ onClose, onSave, schoolClasses, editItem, schoolId, prefill, chapters }) {
  const isEdit = !!editItem;
  const [form, setForm] = useState({
    title:            editItem?.title || prefill?.title || "",
    description:      editItem?.description || "",
    class_id:         editItem?.class_id || prefill?.class_id || "",
    subject_id:       editItem?.subject_id || prefill?.subject_id || "",
    chapter_id:       editItem?.chapter_id || prefill?.chapter_id || "",
    scheduled_at:     editItem?.scheduled_at ? editItem.scheduled_at.slice(0, 16) : "",
    duration_minutes: editItem?.duration_minutes || 60,
    is_recorded:      editItem?.is_recorded || false,
  });
  const [saving, setSaving] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Auto-fetch subjects when class changes
  useEffect(() => {
    if (!form.class_id) { setSubjects([]); set("subject_id", ""); return; }
    lmsGetClassSubjects({ class_id: form.class_id, school_id: schoolId })
      .then((res) => setSubjects(res.data.data || res.data || []))
      .catch(() => setSubjects([]));
  }, [form.class_id, schoolId]);

  // Pre-load subjects when editing an existing item that already has a class
  useEffect(() => {
    if (editItem?.class_id && schoolId) {
      lmsGetClassSubjects({ class_id: editItem.class_id, school_id: schoolId })
        .then((res) => setSubjects(res.data.data || res.data || []))
        .catch(() => {});
    }
  }, []);

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 16, width: 520, maxWidth: "94vw",
        boxShadow: "0 24px 60px rgba(0,0,0,0.18)", overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{ height: 4, background: "linear-gradient(90deg,#4f46e5,#06b6d4)" }} />
        <div style={{
          padding: "18px 22px", borderBottom: "1px solid #f1f5f9",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, background: "#ede9fe",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Video size={18} color="#4f46e5" />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>
                {isEdit ? "Edit Live Class" : "Schedule Live Class"}
              </div>
              <div style={{ fontSize: 12, color: "#9ca3af" }}>BigBlueButton Session</div>
            </div>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#9ca3af" }}>
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <div style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
              Class Title *
            </label>
            <input style={inp()} value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="e.g. Chapter 5 — Motion & Forces" />
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
              Description
            </label>
            <textarea style={inp({ resize: "vertical", minHeight: 60 })}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="What will be covered in this session?" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
                Class
              </label>
              <select style={inp()} value={form.class_id} onChange={(e) => set("class_id", e.target.value)}>
                <option value="">All Classes</option>
                {schoolClasses.map((c) => (
                  <option key={c.class_id ?? c.id} value={String(c.class_id ?? c.id)}>
                    {c.class_name ?? c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
                Subject
              </label>
              <select
                style={inp({ color: form.subject_id ? "#111827" : "#9ca3af" })}
                value={form.subject_id}
                onChange={(e) => set("subject_id", e.target.value)}
                disabled={!form.class_id}
              >
                <option value="">{form.class_id ? "All Subjects" : "Select class first"}</option>
                {subjects.map((s) => (
                  <option key={s.subject_id ?? s.id} value={String(s.subject_id ?? s.id)}>
                    {s.subject_name ?? s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
                Scheduled Date & Time
              </label>
              <input type="datetime-local" style={inp()} value={form.scheduled_at}
                onChange={(e) => set("scheduled_at", e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
                Duration (minutes)
              </label>
              <input type="number" style={inp()} value={form.duration_minutes} min={15} step={15}
                onChange={(e) => set("duration_minutes", parseInt(e.target.value) || 60)} />
            </div>
          </div>

          {/* Record toggle */}
          <label style={{
            display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
            padding: "10px 14px", borderRadius: 10, border: "1.5px solid #e5e7eb",
            background: form.is_recorded ? "#f0fdf4" : "#fafafa",
          }}>
            <input type="checkbox" checked={!!form.is_recorded}
              onChange={(e) => set("is_recorded", e.target.checked)}
              style={{ width: 16, height: 16, accentColor: "#16a34a" }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Record this session</div>
              <div style={{ fontSize: 11, color: "#9ca3af" }}>Recording will be available after class ends</div>
            </div>
          </label>
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 22px 20px", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{
            padding: "9px 20px", borderRadius: 9, fontSize: 13, fontWeight: 600,
            border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{
            padding: "9px 22px", borderRadius: 9, fontSize: 13, fontWeight: 700,
            border: "none", background: "linear-gradient(135deg,#4f46e5,#06b6d4)",
            color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
          }}>
            {saving ? <Loader size={13} className="ms2-spin" /> : <Video size={13} />}
            {isEdit ? "Update" : "Schedule Class"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══ Join Modal ══ */
export function JoinModal({ liveClass, onClose, studentMode = false }) {
  const getStoredName = () => {
    try {
      const u = JSON.parse(localStorage.getItem("userData") || "{}");
      // Try admin_name (set for both admin and student accounts)
      if (u.admin_name) return u.admin_name;
      // Try student_details JSON field (student accounts)
      if (u.student_details) {
        try {
          const sd = typeof u.student_details === "string"
            ? JSON.parse(u.student_details)
            : u.student_details;
          const name = `${sd.first_name || ""} ${sd.last_name || ""}`.trim();
          if (name) return name;
        } catch {}
      }
      // Fallbacks
      const fname = `${u.first_name || ""} ${u.last_name || ""}`.trim();
      return fname || u.name || u.username || u.school_name || "";
    } catch { return ""; }
  };
  const [fullName, setFullName] = useState(getStoredName);
  const [role, setRole]         = useState("attendee");
  const [loading, setLoading]   = useState(false);

  const handleJoin = async () => {
    if (!fullName.trim()) return toast.error("Enter your name to join");
    setLoading(true);
    try {
      const res = await lmsGetLiveClassJoinUrl(liveClass.id, { full_name: fullName.trim(), role });
      const { join_url } = res.data;
      if (!join_url) return toast.error("Could not get join URL — check server BBB config");
      window.open(join_url, "_blank", "noopener,noreferrer");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to get join link");
    } finally { setLoading(false); }
  };

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 16, width: 420, maxWidth: "94vw",
        boxShadow: "0 24px 60px rgba(0,0,0,0.18)", overflow: "hidden",
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#16a34a,#06b6d4)" }} />
        <div style={{ padding: "20px 22px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, background: "#f0fdf4",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Play size={18} color="#16a34a" />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>Join Live Class</div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>{liveClass.title}</div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
                Your Full Name *
              </label>
              <input style={inp({ background: "#f3f4f6", color: "#6b7280", cursor: "not-allowed" })}
                value={fullName} readOnly disabled
                placeholder="Enter your name as it will appear in class" />
            </div>

            {!studentMode && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
                  Join As
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {[
                    { val: "attendee",  label: "Student / Viewer", icon: Users,   color: "#4f46e5" },
                    { val: "moderator", label: "Teacher / Host",   icon: Monitor, color: "#0891b2" },
                  ].map(({ val, label, icon: Icon, color }) => (
                    <label key={val} style={{
                      display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                      padding: "10px 12px", borderRadius: 9,
                      border: `2px solid ${role === val ? color : "#e5e7eb"}`,
                      background: role === val ? `${color}10` : "#fafafa",
                    }}>
                      <input type="radio" name="role" value={val} checked={role === val}
                        onChange={() => setRole(val)} style={{ accentColor: color }} />
                      <Icon size={14} color={color} />
                      <span style={{ fontSize: 12, fontWeight: 600, color: role === val ? color : "#6b7280" }}>
                        {label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
            <button onClick={onClose} style={{
              flex: 1, padding: "10px", borderRadius: 9, fontSize: 13, fontWeight: 600,
              border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", cursor: "pointer",
            }}>Cancel</button>
            <button onClick={handleJoin} disabled={loading} style={{
              flex: 2, padding: "10px", borderRadius: 9, fontSize: 13, fontWeight: 700,
              border: "none", background: "linear-gradient(135deg,#16a34a,#059669)",
              color: "#fff", cursor: "pointer", display: "flex", alignItems: "center",
              justifyContent: "center", gap: 6,
            }}>
              {loading ? <Loader size={13} className="ms2-spin" /> : <ExternalLink size={13} />}
              Join Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══ Confirm Delete Modal ══ */
function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div className="ms2-modal-overlay" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 14, width: 360, maxWidth: "92vw",
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)", overflow: "hidden",
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#dc2626,#ef4444)" }} />
        <div style={{ padding: "24px 22px", textAlign: "center" }}>
          <div style={{
            width: 48, height: 48, borderRadius: "50%", background: "#fef2f2",
            border: "2px solid #fecaca", display: "flex", alignItems: "center",
            justifyContent: "center", margin: "0 auto 12px",
          }}>
            <Trash2 size={20} color="#dc2626" />
          </div>
          <div style={{ fontSize: 14, color: "#374151" }}>{message}</div>
        </div>
        <div style={{ display: "flex", gap: 8, padding: "0 22px 18px" }}>
          <button onClick={onCancel} style={{
            flex: 1, padding: "9px", borderRadius: 8, fontSize: 13, fontWeight: 600,
            border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            flex: 1, padding: "9px", borderRadius: 8, fontSize: 13, fontWeight: 700,
            border: "none", background: "#dc2626", color: "#fff", cursor: "pointer",
          }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ══ Live Class Card ══ */
function LiveClassCard({ item, onJoin, onEdit, onDelete, onRefreshStatus }) {
  const meta = STATUS_META[item.status] || STATUS_META.scheduled;
  const StatusIcon = meta.icon;
  const [checkingStatus, setCheckingStatus] = useState(false);

  const handleCheckStatus = async () => {
    setCheckingStatus(true);
    try {
      const res = await lmsGetLiveClassStatus(item.id);
      const { is_running } = res.data;
      toast.info(is_running ? "Meeting is currently LIVE on BBB" : "Meeting is not running on BBB");
      if (is_running && item.status !== "live") onRefreshStatus();
    } catch { toast.error("Could not check status"); }
    finally { setCheckingStatus(false); }
  };

  return (
    <div style={{
      background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb",
      boxShadow: "0 2px 8px rgba(0,0,0,0.05)", overflow: "hidden",
    }}>
      {/* Top accent */}
      <div style={{
        height: 3,
        background: item.status === "live"
          ? "linear-gradient(90deg,#16a34a,#22c55e)"
          : item.status === "ended"
            ? "#d1d5db"
            : "linear-gradient(90deg,#4f46e5,#06b6d4)",
      }} />

      <div style={{ padding: "16px 18px" }}>
        {/* Title row */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 11, flexShrink: 0,
            background: item.status === "live" ? "#f0fdf4" : "#ede9fe",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {item.status === "live"
              ? <Radio size={20} color="#16a34a" />
              : <Video size={20} color="#4f46e5" />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontWeight: 800, fontSize: 15, color: "#111827",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {item.title}
            </div>
            {item.description && (
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, lineHeight: 1.5 }}>
                {item.description}
              </div>
            )}
          </div>
          {/* Status badge */}
          <span style={{
            flexShrink: 0, fontSize: 11, fontWeight: 700, padding: "3px 9px",
            borderRadius: 20, background: meta.bg, color: meta.color,
            border: `1px solid ${meta.color}33`,
            display: "flex", alignItems: "center", gap: 4,
          }}>
            <StatusIcon size={10} />
            {meta.label}
          </span>
        </div>

        {/* Info pills */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
          {item.class_name && (
            <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280",
              background: "#f3f4f6", borderRadius: 6, padding: "2px 8px" }}>
              {item.class_name}
            </span>
          )}
          {item.subject_name && (
            <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280",
              background: "#f3f4f6", borderRadius: 6, padding: "2px 8px" }}>
              {item.subject_name}
            </span>
          )}
          {item.scheduled_at && (
            <span style={{ fontSize: 11, fontWeight: 600, color: "#4f46e5",
              background: "#ede9fe", borderRadius: 6, padding: "2px 8px",
              display: "flex", alignItems: "center", gap: 3 }}>
              <Calendar size={10} /> {formatDate(item.scheduled_at)}
            </span>
          )}
          <span style={{ fontSize: 11, fontWeight: 600, color: "#0891b2",
            background: "#e0f2fe", borderRadius: 6, padding: "2px 8px",
            display: "flex", alignItems: "center", gap: 3 }}>
            <Clock size={10} /> {formatDuration(item.duration_minutes)}
          </span>
          {item.is_recorded && (
            <span style={{ fontSize: 11, fontWeight: 600, color: "#dc2626",
              background: "#fef2f2", borderRadius: 6, padding: "2px 8px" }}>
              ⏺ Recorded
            </span>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {/* Join button */}
          <button onClick={() => onJoin(item)} style={{
            flex: 1, minWidth: 120, padding: "9px 14px", borderRadius: 9,
            border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer",
            background: item.status === "ended"
              ? "#f3f4f6"
              : "linear-gradient(135deg,#16a34a,#059669)",
            color: item.status === "ended" ? "#9ca3af" : "#fff",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            pointerEvents: item.status === "ended" ? "none" : "auto",
          }}>
            <Play size={13} />
            {item.status === "live" ? "Join Live" : item.status === "ended" ? "Class Ended" : "Start / Join"}
          </button>

          {/* Check status */}
          <button onClick={handleCheckStatus} disabled={checkingStatus} title="Check live status on BBB" style={{
            padding: "9px 11px", borderRadius: 9, border: "1.5px solid #e5e7eb",
            background: "#fff", color: "#6b7280", cursor: "pointer",
            display: "flex", alignItems: "center",
          }}>
            {checkingStatus ? <Loader size={13} className="ms2-spin" /> : <RefreshCw size={13} />}
          </button>

          {/* Edit */}
          <button onClick={() => onEdit(item)} title="Edit" style={{
            padding: "9px 11px", borderRadius: 9, border: "1.5px solid #e0e7ff",
            background: "#f0f4ff", color: "#4f46e5", cursor: "pointer",
            display: "flex", alignItems: "center",
          }}>
            <BookOpen size={13} />
          </button>

          {/* Delete */}
          <button onClick={() => onDelete(item)} title="Delete" style={{
            padding: "9px 11px", borderRadius: 9, border: "1.5px solid #fee2e2",
            background: "#fff5f5", color: "#dc2626", cursor: "pointer",
            display: "flex", alignItems: "center",
          }}>
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════
   MAIN PAGE
══════════════════════════════ */
export default function LiveClasses() {
  const dispatch = useDispatch();
  const schoolId = useSelector((s) => s.auth?.user?.school_id);
  const schoolClasses = useSelector((s) => s.lms?.schoolClasses || []);

  const [classes, setClasses]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filter, setFilter]           = useState("all");     // all | scheduled | live | ended
  const [showSchedule, setShowSchedule] = useState(false);
  const [editItem, setEditItem]         = useState(null);
  const [joinItem, setJoinItem]         = useState(null);
  const [confirmDel, setConfirmDel]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { school_id: schoolId };
      if (filter !== "all") params.status = filter;
      const res = await lmsGetLiveClasses(params);
      setClasses(res.data.data || []);
    } catch { toast.error("Failed to load live classes"); }
    finally { setLoading(false); }
  }, [schoolId, filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (schoolId) dispatch(fetchLmsSchoolClasses(schoolId)); }, [schoolId, dispatch]);

  const handleSave = async (form) => {
    if (editItem) {
      await lmsUpdateLiveClass(editItem.id, { ...form, school_id: schoolId });
      toast.success("Class updated");
    } else {
      await lmsCreateLiveClass({ ...form, school_id: schoolId });
      toast.success("Class scheduled!");
    }
    setEditItem(null);
    load();
  };

  const handleDelete = async () => {
    try {
      await lmsDeleteLiveClass(confirmDel.id);
      toast.success("Class deleted");
      setConfirmDel(null);
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const liveCount      = classes.filter((c) => c.status === "live").length;
  const scheduledCount = classes.filter((c) => c.status === "scheduled").length;

  return (
    <div className="lms-content-inner">
      {/* Header */}
      <div style={{
        background: "#fff", borderRadius: 14, marginBottom: 20,
        border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        overflow: "hidden",
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#4f46e5,#06b6d4,#16a34a)" }} />
        <div style={{ padding: "18px 22px", display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{
            width: 46, height: 46, borderRadius: 12, background: "linear-gradient(135deg,#4f46e5,#06b6d4)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <Video size={22} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 18, color: "#111827" }}>Live Classes</div>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
              Powered by BigBlueButton — schedule, host & record live sessions
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: "flex", gap: 20, flexShrink: 0 }}>
            {liveCount > 0 && (
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#16a34a" }}>{liveCount}</div>
                <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600 }}>LIVE NOW</div>
              </div>
            )}
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#4f46e5" }}>{scheduledCount}</div>
              <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600 }}>UPCOMING</div>
            </div>
          </div>

          <button onClick={() => { setEditItem(null); setShowSchedule(true); }} style={{
            display: "flex", alignItems: "center", gap: 7, padding: "10px 18px",
            borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,#4f46e5,#06b6d4)",
            color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}>
            <Plus size={15} /> Schedule Class
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {[
          { val: "all",       label: "All Classes" },
          { val: "live",      label: "🔴 Live Now" },
          { val: "scheduled", label: "Upcoming" },
          { val: "ended",     label: "Ended" },
        ].map(({ val, label }) => (
          <button key={val} onClick={() => setFilter(val)} style={{
            padding: "7px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
            border: `1.5px solid ${filter === val ? "#4f46e5" : "#e5e7eb"}`,
            background: filter === val ? "#4f46e5" : "#fff",
            color: filter === val ? "#fff" : "#6b7280",
            cursor: "pointer",
          }}>{label}</button>
        ))}
        <button onClick={load} style={{
          marginLeft: "auto", padding: "7px 12px", borderRadius: 8,
          border: "1.5px solid #e5e7eb", background: "#fff", color: "#6b7280",
          cursor: "pointer", display: "flex", alignItems: "center", gap: 5, fontSize: 13,
        }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <Loader size={28} className="ms2-spin" color="#4f46e5" />
        </div>
      ) : classes.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "60px 20px",
          background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb",
        }}>
          <Video size={48} color="#e5e7eb" style={{ margin: "0 auto 16px" }} />
          <div style={{ fontSize: 16, fontWeight: 700, color: "#374151", marginBottom: 6 }}>
            {filter === "live" ? "No live sessions right now" : "No classes found"}
          </div>
          <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 20 }}>
            Schedule a BigBlueButton live session for your students.
          </div>
          <button onClick={() => { setEditItem(null); setShowSchedule(true); }} style={{
            padding: "10px 24px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,#4f46e5,#06b6d4)",
            color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}>+ Schedule First Class</button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 16 }}>
          {classes.map((item) => (
            <LiveClassCard
              key={item.id}
              item={item}
              onJoin={() => setJoinItem(item)}
              onEdit={(i) => { setEditItem(i); setShowSchedule(true); }}
              onDelete={setConfirmDel}
              onRefreshStatus={load}
            />
          ))}
        </div>
      )}


      {/* Modals */}
      {showSchedule && (
        <ScheduleModal
          onClose={() => { setShowSchedule(false); setEditItem(null); }}
          onSave={handleSave}
          schoolClasses={schoolClasses}
          editItem={editItem}
          schoolId={schoolId}
        />
      )}
      {joinItem && (
        <JoinModal liveClass={joinItem} onClose={() => setJoinItem(null)} />
      )}
      {confirmDel && (
        <ConfirmModal
          message={<>Delete <strong>{confirmDel.title}</strong>? This will also end the BBB meeting.</>}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDel(null)}
        />
      )}
    </div>
  );
}
